import { FC, memo, useCallback, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  DialogActions,
  DialogContent,
  FormControl,
  Typography,
} from "@material-ui/core";
import { closeDrawer } from "actions/drawerActions";
import { useDispatch } from "react-redux";
import { CustomButton } from "componentsNew/buttons/CustomButton";
import { useStyles } from "../../../pages/ShiftReports/styles";
import { shiftTrackerUpdateFilters } from "actions/shiftTrackerActions";
import ExpandMore from "@material-ui/icons/ExpandMore";
import { SHIFT_REPORT_FILTER_SETTINGS_LIST } from "pages/ShiftReports/tableData";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { ReportFiltersParams } from "types/ShiftTracker";
import { DateFilter } from "componentsNew/elements/DateFilter";
import { DateRangeSelector } from "componentsNew/elements/DateRangeSelector";
import api from "api";
import { SelectApiField } from "componentsNew/elements/SelectApiField";

interface ShiftTrackerFiltersDrawerContentProps {}

interface FilterFieldComponentProps {
  id: keyof ReportFiltersParams;
  initialValue: ReportFiltersParams[keyof ReportFiltersParams];
  onChange: (
    id: keyof ReportFiltersParams,
    value: ReportFiltersParams[keyof ReportFiltersParams],
  ) => void;
}

interface FilterAccordionSectionProps {
  item: (typeof SHIFT_REPORT_FILTER_SETTINGS_LIST)[number];
  initialFilters: ReportFiltersParams;
  onChange: FilterFieldComponentProps["onChange"];
}

const hasAssignedValue = (value: unknown): boolean => {
  if (value == null || value === "" || value === 0) return false;
  if (Array.isArray(value)) return value.some((item) => hasAssignedValue(item));
  return true;
};

const DEFAULT_FILTERS: ReportFiltersParams = {
  shiftDate: "",
  shiftDateRange: "",
  week: "",
  month: "",
  year: null,
  fleetVendor: null,
  fleetNumber: null,
  fleetSize: null,
  station: 1,
  driver: null,
  dollySerial: null,
  rabbitSerial: null,
  userCreated: null,
  userUpdated: null,
} as any;

const getVans = () =>
  api.getVans({
    page: 1,
    page_size: 600,
  });
const getVanGroups = () => api.getVanGroups();
const getFleetSizes = () => api.getFleetSizes();
const getDriverList = () => api.getDriverList();
const getRabbit = () => api.getRabbitNumbers();
const getDolly = () => api.getDollyNumbers();
const getUsersByCreatedOptions = () => api.getUsers({ by_create: true });
const getUsersByUpdatedOptions = () => api.getUsers({ by_update: true });

export const ShiftTrackerFiltersDrawerContent: FC<ShiftTrackerFiltersDrawerContentProps> = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { reportListFilters } = useTypeSelectors((state) => state.dasBoard);
  const [initialFilters] = useState<ReportFiltersParams>(reportListFilters || DEFAULT_FILTERS);
  const allFiltersRef = useRef<ReportFiltersParams>(initialFilters);

  const closenModal = () => dispatch(closeDrawer());

  const handleFieldChange = useCallback(
    (id: keyof ReportFiltersParams, value: ReportFiltersParams[keyof ReportFiltersParams]) => {
      allFiltersRef.current = {
        ...allFiltersRef.current,
        [id]: value,
      };
    },
    [],
  );

  const handleFilterSubmit = () => {
    dispatch(shiftTrackerUpdateFilters(allFiltersRef.current));
    closenModal();
  };

  const handleReset = () => {
    dispatch(
      shiftTrackerUpdateFilters({
        shiftDate: "",
        shiftDateRange: ["", ""],
        week: "",
        month: "",
        year: null,
        fleetVendor: null,
        fleetNumber: null,
        fleetSize: null,
        station: 1,
        driver: null,
        dollySerial: null,
        rabbitSerial: null,
        userCreated: null,
        userUpdated: null,
      }),
    );
    closenModal();
  };

  return (
    <>
      <DialogContent className={classes.drawerContent}>
        {SHIFT_REPORT_FILTER_SETTINGS_LIST.map((item) => (
          <FilterAccordionSection
            key={item.id}
            item={item}
            initialFilters={initialFilters}
            onChange={handleFieldChange}
          />
        ))}
      </DialogContent>
      <DialogActions className={classes.actionSection}>
        <Box className={classes.actionButtons}>
          <Box className={classes.actionButtons}>
            <Box className={classes.actionButtonsLeft}>
              <CustomButton buttonText={"Save"} onClick={handleFilterSubmit} variant="contained" />
              <CustomButton buttonText={"Cancel"} onClick={closenModal} variant="text" />
            </Box>
            <CustomButton buttonText={"Reset"} onClick={handleReset} variant="text" />
          </Box>
        </Box>
      </DialogActions>
    </>
  );
};

const FilterAccordionSection = memo(
  ({ item, initialFilters, onChange }: FilterAccordionSectionProps) => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(() =>
      item.fields.some((field) =>
        hasAssignedValue(initialFilters[field.id as keyof ReportFiltersParams]),
      ),
    );

    const handleFieldChange = useCallback(
      (id: keyof ReportFiltersParams, value: ReportFiltersParams[keyof ReportFiltersParams]) => {
        if (hasAssignedValue(value)) setExpanded(true);
        onChange(id, value);
      },
      [onChange],
    );

    return (
      <Accordion
        className={classes.accordionItem}
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls={`${item.id}-panel1-content`}
          id={`${item.id}-panel1-header`}
        >
          <Typography>{item.label}</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.formBox}>
          {item.fields.map((innerItem) => (
            <FormControl key={innerItem.id} fullWidth>
              <Typography className={classes.textLabel}>{innerItem.label}</Typography>
              <FilterFieldComponent
                id={innerItem.id as keyof ReportFiltersParams}
                initialValue={initialFilters[innerItem.id as keyof ReportFiltersParams]}
                onChange={handleFieldChange}
              />
            </FormControl>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  },
);

const FilterFieldComponent = memo(({ id, initialValue, onChange }: FilterFieldComponentProps) => {
  const [value, setValue] = useState(initialValue);

  const handleChange = useCallback(
    (newValue: any) => {
      setValue(newValue);
      onChange(id, newValue);
    },
    [id, onChange],
  );

  switch (id) {
    case "shiftDate":
      return (
        <DateFilter
          fullWidth
          type={"date"}
          dateValue={value as string}
          dateChange={handleChange as any}
        />
      );
    case "shiftDateRange":
      return <DateRangeSelector value={value} setValue={handleChange as any} />;
    case "week":
      return (
        <DateFilter
          fullWidth
          type={"week"}
          weekValue={value as string}
          weekChange={handleChange as any}
        />
      );
    case "month":
      return (
        <DateFilter
          fullWidth
          type={"month"}
          monthValue={value as string}
          monthChange={handleChange as any}
        />
      );
    case "year":
      return (
        <DateFilter
          fullWidth
          type={"year"}
          yearValue={value as string}
          yearChange={handleChange as any}
        />
      );
    case "fleetVendor":
      return (
        <SelectApiField
          value={value as number | null}
          onChange={handleChange}
          api={getVanGroups}
          renderValue={(val: any) => val.name}
        />
      );
    case "fleetNumber":
      return (
        <SelectApiField
          value={value as number | null}
          onChange={handleChange}
          api={getVans}
          renderValue={(val: any) => val.number}
        />
      );
    case "fleetSize":
      return (
        <SelectApiField
          value={value as number | null}
          onChange={handleChange}
          api={getFleetSizes}
          renderValue={(val: any) => val.title}
        />
      );
    case "driver":
      return (
        <SelectApiField
          value={value as number | null}
          onChange={handleChange}
          api={getDriverList}
          renderValue={(val: any) => `${val.first_name} ${val.last_name}`}
        />
      );
    case "rabbitSerial":
      return (
        <SelectApiField
          value={value as number | null}
          onChange={handleChange}
          api={getRabbit}
          renderValue={(val: any) => val.serial_number}
        />
      );
    case "dollySerial":
      return (
        <SelectApiField
          value={value as number | null}
          onChange={handleChange}
          api={getDolly}
          renderValue={(val: any) => val.serial_number}
        />
      );
    case "userCreated":
      return (
        <SelectApiField
          value={value as number | null}
          onChange={handleChange}
          api={getUsersByCreatedOptions}
          renderValue={(val: any) => `${val.first_name} ${val.last_name}`}
        />
      );
    case "userUpdated":
      return (
        <SelectApiField
          value={value as number | null}
          onChange={handleChange}
          api={getUsersByUpdatedOptions}
          renderValue={(val: any) => `${val.first_name} ${val.last_name}`}
        />
      );
    default:
      return <></>;
  }
});
